// Shared case-study data source — used across home carousel, /work grid, and /work/:id detail.
// Content sourced from the actual dotConverse case-study deck (May 2026 batch 1).

export const caseStudies = [
  {
    id: "growth-98",
    cat: "SEO",
    tag: "Tech Comparison Platform",
    client: "Global tech comparison platform",
    title: "The 98% Growth Playbook",
    subtitle:
      "How a global tech-comparison platform out-ranked media giants and grew organic sessions 98% in 12 months — with technical SEO depth and AI-assisted long-tail content at scale.",
    stat: "+98%",
    statLabel: "Organic sessions",
    accent: "#FFB000",
    duration: "12 months",
    region: "India · Global",
    services: ["SEO", "Technical SEO", "AI Content Scale"],
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Tech comparison is a brutal SERP — dominated by media giants with decades of authority. The client needed to out-rank them for transactional pricing queries, scale long-tail coverage fast, and fix a thicket of crawl-and-index problems holding the site back.",
    approach: [
      "Fixed core technical SEO debt — redirect chains, orphan pages, canonical issues — and rebuilt site speed and mobile responsiveness.",
      "Deployed Python-driven indexing automation and monthly sitemap re-submissions to handle URL-scale.",
      "Spun up an AI + human content engine for long-tail laptop, spec, and price queries; revamped existing top pages.",
      "Continuously tracked rankings, fixed new orphan pages, and ran competitor gap analysis every month.",
    ],
    outcomes: [
      { k: "+98%", v: "Organic sessions" },
      { k: "5×", v: "Pages indexed" },
      { k: "8.6×", v: "Keywords in Top 20" },
      { k: "+71%", v: "Top-50 rankings" },
    ],
    conclusion:
      "Across 12 months, organic sessions grew 98%, pages indexed grew 5×, keywords in the Top 20 grew 8.6×, and Top-50 rankings grew 71% — built on a foundation of fixed technical SEO debt, Python-driven indexing automation, and an AI + human long-tail content engine.",
  },
  {
    id: "enterprise-22",
    cat: "Demand Gen",
    tag: "Video Conferencing · Enterprise",
    client: "Global Swiss video-conferencing brand",
    title: "3,500 Clicks. 22 Enterprise Deals.",
    subtitle:
      "How precision targeting and the RightLeadz framework turned cold B2B traffic into ₹2.85 Cr of enterprise pipeline.",
    stat: "22",
    statLabel: "Enterprise deals",
    accent: "#6B77E8",
    duration: "9 months",
    region: "India · APAC",
    services: ["Demand Generation", "Lead Qualification", "Sales Enablement"],
    img: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "The Indian video conferencing market is flooded with cheap suppliers, putting brutal pressure on premium positioning. The client needed to capture high-intent leads — not just clicks — qualify hard, and translate every rupee of media spend into actual pipeline.",
    approach: [
      "Ran segmented Google search + interest-based social ads targeting CXOs, CTOs, IT Directors, and Procurement Heads across BFSI, IT/ITES, Manufacturing, and Education.",
      "Deployed the RightLeadz framework — conversion-optimised landing pages and a Virtual Demand Gen team manually enriching every inquiry for industry, role, and meeting-room scale.",
      "Scored leads as SQL or SAL based on sales readiness and prioritised accounts for immediate revenue-manager follow-up.",
      "Owned the full funnel — strategy, creative, execution, qualification, and hand-off.",
    ],
    outcomes: [
      { k: "22", v: "Deals closed" },
      { k: "175", v: "Sales-qualified leads" },
      { k: "₹2.85 Cr", v: "Pipeline value" },
      { k: "884", v: "Total enriched leads" },
    ],
    conclusion:
      "884 enriched leads, 175 sales-qualified opportunities, 22 closed enterprise deals, and ₹2.85 Cr in pipeline — all owned end-to-end by a single integrated RightLeadz program covering strategy, segmented targeting across CXOs, CTOs, IT Directors, and Procurement Heads, manual lead enrichment, and sales hand-off.",
  },
  {
    id: "smart-workplace-abm",
    cat: "ABM",
    tag: "Smart Workplace · Wireless Conferencing",
    client: "Global smart-workplace tech leader",
    title: "Hyper-Personalised ABM for the C-Suite",
    subtitle:
      "A 'Reinvent Your Workplace' content engine that engaged 7,800+ decision-makers across 3,700 accounts and built €80K of qualified ABM pipeline.",
    stat: "24%",
    statLabel: "DMs mapped with intent",
    accent: "#0FB5A6",
    duration: "12 months",
    region: "EMEA · APAC",
    services: ["ABM", "Content Personalisation", "Buying-Journey Scoring"],
    img: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Smart-workplace tech is a noisy, generic-messaging category — and CXOs simply don't respond to cold ads. The client needed buyer-journey mapping, deep content personalisation, and account-level scoring that could run at scale across thousands of target accounts.",
    approach: [
      "Designed the 'Reinvent Your Workplace' content theme and mapped the buyer journey across four stages — Awareness, Engagement, Advanced Engagement, Action.",
      "Shipped 100 role-and-industry microsites, 100+ articles, 60+ social posts, 45 infographics, and 10 online banners across 3,700 accounts.",
      "Created sharp context-loaded pieces like 'The Future of Open Office', 'How Cloud Can Wipe Out Schools by 2025', and product-positioned hooks around stress-free meeting rooms.",
      "Used KABOOM's intent-scoring to flag sales-ready accounts and hand them to revenue managers with full journey context.",
    ],
    outcomes: [
      { k: "7,800+", v: "Decision-makers engaged" },
      { k: "24%", v: "DMs mapped with intent" },
      { k: "€80K", v: "Pipeline identified" },
      { k: "11", v: "Accounts converted" },
    ],
    conclusion:
      "Across 3,700 target accounts, 7,800+ decision-makers were engaged, 24% were mapped with active intent, 11 accounts converted, and €80K of qualified ABM pipeline was identified — all built on the 'Reinvent Your Workplace' content theme, 100 role-and-industry microsites, 100+ articles, and KABOOM's intent-scoring framework.",
  },
  {
    id: "collab-giant",
    cat: "ABM",
    tag: "Video Collaboration · Key Accounts",
    client: "Global video collaboration leader",
    title: "Unlocking Key Accounts for a Collaboration Giant",
    subtitle:
      "A 'Happy at Work' ABM engine that tracked 9,425 contacts, scored 31% of them, and surfaced 72 sales-ready accounts.",
    stat: "₹85L",
    statLabel: "Pipeline identified",
    accent: "#E96A7F",
    duration: "11 months",
    region: "India",
    services: ["ABM", "Content Personalisation", "Intent Scoring"],
    img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Enterprise video conferencing buyers move slow and rarely convert from cold outreach. With a cluttered market and a noisy inbox, the client needed to identify decision-makers in target accounts and steer them through to a real sales conversation.",
    approach: [
      "Built a 'Happy at Work' communication hook — positioning the brand's video products as the antidote to travel stress and as enablers of flexible, joyful workplaces.",
      "Deployed the three pillars of KABOOM ABM — content personalisation, journey tracking, and intent + engagement scoring — across role-specific creatives with dynamic CTAs.",
      "Tracked every interaction at account and contact level, scored intent, and enriched the dataset with industry, company size, seniority, and function.",
      "Surfaced 72 hot accounts in the Action stage for immediate sales action.",
    ],
    outcomes: [
      { k: "9,425", v: "Contacts tracked" },
      { k: "31%", v: "Mapped & scored" },
      { k: "72", v: "Sales-ready accounts" },
      { k: "₹85L", v: "Pipeline identified" },
    ],
    conclusion:
      "9,425 contacts tracked at account and contact level, 31% mapped and scored across the buying journey, 72 sales-ready accounts surfaced in the Action stage, and ₹85L of pipeline identified — built on the 'Happy at Work' theme and KABOOM's three pillars of content personalisation, journey tracking, and intent + engagement scoring.",
  },
  {
    id: "erp-cloud",
    cat: "Demand Gen",
    tag: "Cloud ERP · Enterprise",
    client: "Global cloud & ERP leader",
    title: "98 Meetings. 272 Qualified Leads.",
    subtitle:
      "A verticalised RightLeadz engine turning slow, complex ERP buying journeys into 272 qualified leads and 98 sales meetings — with 95% showing 0–3 month buying intent.",
    stat: "98",
    statLabel: "Sales meetings",
    accent: "#B7D455",
    duration: "10 months",
    region: "Global",
    services: ["Demand Generation", "Vertical Targeting", "Funnel Scoring"],
    img: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Cloud ERP buying is slow, complex, and CXO-led — across Manufacturing, Pharma, HR, Retail, and Professional Services. The client wasn't hunting for clicks. They needed precision targeting, dramatically lower funnel drop-offs, and leads that were genuinely meeting-ready — not vanity MQLs.",
    approach: [
      "Launched the RightLeadz program with industry-specific ad creatives, landing pages, and use-case stories per vertical.",
      "Enriched every lead with company size, designation, ERP-tool usage, and intent stage — then scored against the buying journey.",
      "Ran ERP-stack analysis to give sales teams competitive intelligence ahead of every conversation.",
      "Owned the full funnel end-to-end — strategy, creative, execution, qualification, hand-off — instead of dumping leads.",
    ],
    outcomes: [
      { k: "272", v: "Qualified leads" },
      { k: "98", v: "Sales meetings" },
      { k: "95%", v: "0–3 month buying intent" },
      { k: "23", v: "Ebook downloads" },
    ],
    conclusion:
      "272 qualified leads, 98 sales meetings, 23 ebook downloads, and 95% of leads showing 0–3 month buying intent — delivered through a verticalised RightLeadz engine across Manufacturing, Pharma, HR, Retail, and Professional Services, with industry-specific creative, ERP-stack analysis, and full-funnel ownership end-to-end.",
  },
  {
    id: "wireless-webinar",
    cat: "Demand Gen",
    tag: "Wireless Conferencing · Mid-Funnel Webinar",
    client: "Global wireless conferencing leader",
    title: "A Webinar That C-Suites Actually Watched",
    subtitle:
      "How a precision invite, an interactive 45-minute live demo, and a hand-built nurture engine turned mid-funnel webinar fatigue into 164 live engagements and a 42-minute average view time.",
    stat: "42 min",
    statLabel: "Avg viewing time",
    accent: "#3BA7D1",
    duration: "6 weeks",
    region: "APAC · 2 countries",
    services: ["Demand Generation", "Webinar Strategy", "Mid-Funnel Nurture"],
    img: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Webinar fatigue is real — inboxes are flooded with invites and tech-savvy decision-makers ignore most of them. The client needed precision targeting (only senior tech buyers), genuine live engagement (not vanity registrations), and a session that actually justified 45 minutes of a CXO's calendar.",
    approach: [
      "Designed crisp, product-focused creatives positioning wireless conferencing as the future of meeting rooms — paired with sponsored ads, personalised emails, and a landing page anchored to 'Great Things Happen When People Click'.",
      "Hosted a 45-minute interactive session led by the client's APAC VP of Sales — with a live product demo, real Q&A, and embedded polls capturing prospect pain points in real time.",
      "Powered post-webinar nurture: shared product eBooks, prioritised outreach to live engagers, and built custom remarketing lists for sales follow-up.",
      "Distilled outcomes into a short recap video deployed across the landing page and social channels to extend the long tail.",
    ],
    outcomes: [
      { k: "1 Lakh+", v: "Power users engaged" },
      { k: "850", v: "Webinar registrations" },
      { k: "164", v: "Live Q&A + poll engagements" },
      { k: "42 min", v: "Average viewing time" },
    ],
    conclusion:
      "Over 1 lakh power users engaged, 850 webinar registrations, 164 live Q&A and poll engagements, and a 42-minute average viewing time — shipped in six weeks through a precision invite engine, a 45-minute interactive demo led by the client's APAC VP of Sales, and a hand-built post-event nurture and remarketing flow.",
  },
  {
    id: "twenty-fifth-hour",
    cat: "Demand Gen",
    tag: "Video Conferencing · Brand Campaign",
    client: "Leading video conferencing brand",
    title: "The 25th Hour Goes Viral",
    subtitle:
      "How an abstract storytelling idea — 'what if you had one more hour?' — turned into 800+ corporate entries and 6,000+ social votes, with 65% participant quality maintained throughout.",
    stat: "6,000+",
    statLabel: "Social votes",
    accent: "#B7D455",
    duration: "25 days",
    region: "Global",
    services: ["Brand Campaign", "Demand Generation", "Social Activation"],
    img: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "The '25th Hour' was abstract — not a product push, but a thought-provoking idea positioning the client's video conferencing as a giver of time. The brief was to spark curiosity and corporate participation without diluting premium positioning, capture high-value engagement instead of mass traffic, and balance storytelling with a contest mechanic that didn't sound salesy.",
    approach: [
      "Phase 1 — Teaser (5 days): seeded the idea of an extra hour via targeted emails and banner ads driving to a 'What is the 25th Hour?' microsite that collected early sign-ups.",
      "Phase 2 — Contest launch (5 days): activated the warm audience with 'What would you do with an extra hour?' and a countdown landing page surfacing the client's product as the unlock.",
      "Phase 3 — Social amplification (15 days): drove virality by prompting participants to share their 25th Hour idea and rally votes on a fully-optimised contest microsite.",
      "Maintained creative discipline so the campaign stayed culturally on-brand for a Fortune 1000 corporate audience.",
    ],
    outcomes: [
      { k: "2M+", v: "Audience reach" },
      { k: "4M+", v: "Ad impressions" },
      { k: "800+", v: "Corporate entries" },
      { k: "6,000+", v: "Social votes" },
      { k: "65%", v: "Corporate participant quality" },
      { k: "15%", v: "Top-account engagement" },
    ],
    conclusion:
      "Over 2 million audience reach, 4 million+ ad impressions, 800+ corporate entries, 6,000+ social votes, 65% corporate participant quality, and 15% top-account engagement — delivered through a 25-day, three-phase activation (teaser → contest launch → social amplification) that paired storytelling with a culturally on-brand contest mechanic.",
  },
  {
    id: "steel-125",
    cat: "SEO",
    tag: "TMT & Steel Manufacturing",
    client: "Top India TMT & steel manufacturer",
    title: "125% Growth for an Offline Industry",
    subtitle:
      "How a digital content + technical SEO program built search authority for a leading TMT and steel brand from near-zero — and grew organic sessions 125% in 7 months.",
    stat: "+125%",
    statLabel: "Organic sessions",
    accent: "#FFB000",
    duration: "7 months",
    region: "India",
    services: ["SEO", "Technical SEO", "Content Strategy"],
    img: "https://images.unsplash.com/photo-1565043666747-69f6646db940?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "The TMT steel sector lives offline — built on dealer relationships and decades of brand equity. Very few competitors invest in SEO or digital content. The client needed to build search relevance from scratch, rank against incumbents on competitive manufacturing keywords, and get Google to recognise them as a real digital authority in steel.",
    approach: [
      "Redesigned the site structure and navigation for content + product co-existence; rebuilt speed and mobile responsiveness; configured Python-driven indexing for the new content pipeline.",
      "Created a dedicated content framework for builders, engineers, and contractors — articles plus video clusters spanning product specs and industry use cases.",
      "Ran exhaustive competitor gap analysis, structured pillar + cluster keywords mapped to B2B decision stages, and ran complete on-page SEO across titles, meta, headers.",
      "Tracked engagement, fixed orphan pages, strengthened internal linking, and re-submitted sitemaps every month to keep momentum compounding.",
    ],
    outcomes: [
      { k: "+125%", v: "Organic sessions" },
      { k: "9×", v: "Pages indexed" },
      { k: "2.9×", v: "Keywords in Top 20" },
      { k: "+68%", v: "Top-10 rankings" },
    ],
    conclusion:
      "Across 7 months, organic sessions grew 125% (782 → 1,763), pages indexed grew 9× (6 → 54), keywords in the Top 20 grew 2.9× (10 → 29), and keywords in the Top 10 grew 68% (19 → 32) — built from near-zero digital search authority in a category where competitors rarely invest in SEO or content.",
  },
  {
    id: "finance-300",
    cat: "SEO",
    tag: "Finance & Accounting Education",
    client: "India's top finance & accounting training platform",
    title: "300% SEO Growth in 12 Months",
    subtitle:
      "How AI + human content scale, technical SEO depth, and semantic topic clustering tripled organic sessions, indexed 2,000 new URLs, and unlocked 6,200+ new ranking keywords.",
    stat: "6×",
    statLabel: "Keyword reach",
    accent: "#6B77E8",
    duration: "12 months",
    region: "India",
    services: ["SEO", "EDGE SEO", "AI Content Scale"],
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Finance & accounting education is dominated by high-authority sites with deep legacy. The client needed to climb on punishingly competitive keywords, scale indexed pages fast, and — most importantly — turn SEO into a lead-generating channel for student enrolments, not just a vanity-traffic exercise.",
    approach: [
      "Audited site health and content depth, then fixed link-score, internal linking, redirection chains, canonical issues, crawl depth, and EDGE SEO — restructuring the site for semantic topic clustering.",
      "Blended AI + human writing for career-focused articles, guides, and landing pages; uploaded new content against competitor gap analysis, enriched metadata, and configured Python indexing for scale.",
      "Studied seasonality patterns and aligned publishing cadence to peaks; upgraded navigation for crawlability and engagement.",
      "Continuously optimised page-level hotspots, re-submitted sitemaps, fixed mixed content + orphan pages, and tracked engagement rate to ensure traffic quality held.",
    ],
    outcomes: [
      { k: "+220%", v: "Organic sessions" },
      { k: "6,200+", v: "New ranking keywords" },
      { k: "5.5×", v: "Keywords in Top 20" },
      { k: "3.7×", v: "Keywords in Top 10" },
      { k: "3.3×", v: "Pages indexed" },
    ],
    conclusion:
      "From January to July 2024, organic sessions grew 220% (3,015 → 9,638), pages indexed grew 3.3× (147 → 486), keywords in the Top 10 grew 3.7× (97 → 356), keywords in the Top 20 grew 5.5× (122 → 672), and 6,259 new ranking keywords were added — all powered by EDGE SEO, semantic topic clustering, AI SEO implementation, and a complete technical-SEO overhaul.",
  },
  {
    id: "ai-infra-linkedin",
    cat: "AI",
    tag: "AI Infrastructure · LinkedIn",
    client: "Enterprise AI infrastructure brand",
    title: "5× Industry Engagement on LinkedIn — for a Server Company",
    subtitle:
      "How a 4-pillar LinkedIn engine for an AI infrastructure brand (servers, HPC, AI compute) sustained 8% annual engagement — over five times the tech industry's 1.5% benchmark — and peaked at 11.3% in the strongest month.",
    stat: "11.3%",
    statLabel: "Peak engagement rate",
    accent: "#3BA7D1",
    duration: "12 months",
    region: "India · Global",
    services: ["AI-Powered Marketing", "Demand Generation", "Social Selling"],
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Cloud, compute, and AI servers are notoriously hard to make engaging on social — hardware reads as commodity, and the buyers are slow-moving VPs, academics, and influencers. Worse: the average LinkedIn engagement rate for the technology vertical sits at just 1.5%. The client needed to break that ceiling without diluting brand premium.",
    approach: [
      "Built a 4-pillar LinkedIn strategy — Company Page (use-case + product + tech content), VP-led Social Selling (whitepapers and studies to senior decision-makers), LinkedIn Groups (community-driven content and events), and Private Webinars (educational workshops to influence the influencers).",
      "Designed a content framework that progressed across buying stages — AWARE → Educate → Consideration → Best Practices → Enablement — anchored on Cloud & Compute, AI/ML integration, HPC workloads, and industry-specific use cases (drug discovery, autonomous vehicles, virtual labs).",
      "Mixed format-rich content (carousels, whitepapers, slideshow videos, newsletters, blogs) with 1:1 outreach via VP profiles for senior nurture and mind-share.",
      "Measured engagement velocity and re-tuned the framework monthly to keep performance compounding above 5× industry benchmark.",
    ],
    outcomes: [
      { k: "11.3%", v: "Peak engagement (vs 1.5% industry)" },
      { k: "8%", v: "Sustained annual engagement" },
      { k: "5×", v: "Above industry benchmark" },
      { k: "448", v: "Senior connections established" },
      { k: "28", v: "Positive sales responses" },
    ],
    conclusion:
      "Across 12 months, average LinkedIn engagement held at ~8% and peaked at 11.3% — over five times the technology vertical's 1.5% Hootsuite benchmark — with 2,350 invites sent, 448 senior connections established, and 28 positive sales responses from a 4-pillar LinkedIn strategy spanning Company Page, VP-led Social Selling, LinkedIn Groups, and Private Webinars.",
  },
  {
    id: "loyalty-saas-cpl",
    cat: "Demand Gen",
    tag: "Loyalty SaaS · USA",
    client: "USA-based loyalty SaaS",
    title: "$1,000 CPL → $742 in 30 Days",
    subtitle:
      "A 30-day pre-funding sprint that re-engineered a USA-based loyalty SaaS's paid funnel across Google, LinkedIn, and Bing — cutting cost per qualified lead 26%, lifting CTR to 4.58%, and standing up multi-platform remarketing from scratch.",
    stat: "$742",
    statLabel: "Cost per lead",
    accent: "#E96A7F",
    duration: "30 days",
    region: "USA",
    services: ["Performance Marketing", "Landing Page CRO", "Demand Generation"],
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "The client was burning through media at $1,000+ per qualified lead in a brutally competitive, low-priority SaaS category. Quality scores on Google were tanking thanks to a weak landing page experience, and the team needed to ship dramatic CPL improvements within a 30-day window — ahead of the next round of funding.",
    approach: [
      "Invested 30–45 days in audit and small-scale experimentation before unlocking full media spend — mapping user behaviour and buying stage instead of guessing.",
      "Layered vertical-based targeting + messaging on LinkedIn, intent + remarketing on Google search, and middle-of-funnel long-tail queries to widen the qualified-lead pool.",
      "Rebuilt landing-page flow and aligned messaging tightly with targeting — sharp, personalised, vertical-by-vertical.",
      "Added Bing search to optimise reach and CPL, and stood up integrated remarketing across all platforms with a separate funnel for re-engaged prospects.",
    ],
    outcomes: [
      { k: "$742", v: "CPL (down from $1,000+)" },
      { k: "26%", v: "CPL reduction" },
      { k: "8", v: "Qualified conversions in 30 days" },
      { k: "4.58%", v: "Overall CTR" },
      { k: "30 days", v: "End-to-end execution" },
    ],
    conclusion:
      "In 30 days, cost per qualified lead was brought down from $1,000+ to $742.41 (a 26% reduction), with 999 clicks, 21,809 impressions, a 4.58% CTR, and 8 qualified conversions — delivered through vertical-based LinkedIn targeting, Google intent + remarketing, middle-funnel long-tail queries, Bing search added to the portfolio, and a rebuilt landing-page flow.",
  },
  {
    id: "optics-museum-cx",
    cat: "CX",
    tag: "Premium Optics · Museum Experience",
    client: "Global premium optics brand",
    title: "Redefining Experience: Offline Meets Digital",
    subtitle:
      "An invisible digital layer inside a high-end optics museum — turning every product zone into a story, a demo, and a measurable conversion opportunity, without ever breaking the premium feel.",
    stat: "8",
    statLabel: "Interactive product zones",
    accent: "#0FB5A6",
    duration: "Ongoing",
    region: "India",
    services: ["Customer Experience", "CX Content Platform", "Demand Generation"],
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?q=80&w=2400&auto=format&fit=crop",
    challenge:
      "Premium optical systems can't afford a tacky digital overlay. The brief: create a seamless, unobtrusive digital layer inside a museum space; keep the educational + premium feel intact; and capture rich first-party data without interrupting the art-tech experience.",
    approach: [
      "Identified 8 core products and crafted an interactive story flow for each — product history, lens capability, sample imagery, and explainer videos.",
      "Generated custom QR codes for every product zone; each scan opened a mobile-optimised landing page tailored to that product, with deep-dive media and clear next actions.",
      "Built one-click engagement paths — book a demo, follow the brand, download a brochure, sign up for a workshop, or talk to an expert.",
      "Instrumented the backend to track scans, video consumption, PDF downloads, demo requests, and stored audience cookies for downstream retargeting.",
    ],
    outcomes: [
      { k: "8", v: "Interactive product zones" },
      { k: "100%", v: "Mobile-first experience" },
      { k: "1:1", v: "Story per product flow" },
      { k: "First-party", v: "Audience cookies captured" },
    ],
    conclusion:
      "Eight core products were turned into eight interactive QR-driven story zones — each a mobile-first landing page with product history, lens capability, sample imagery, and explainer videos, plus one-click engagement paths (book a demo, follow the brand, download a brochure, sign up for a workshop, or talk to an expert) — with full backend instrumentation tracking scans, video consumption, downloads, demo requests, and audience cookies for retargeting.",
  },
];

export const findCaseStudy = (id) => caseStudies.find((c) => c.id === id);
export const getRelatedCaseStudies = (id, n = 3) =>
  caseStudies.filter((c) => c.id !== id).slice(0, n);
